import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * The mobile top bar's title, published by whichever page is on screen.
 *
 * Below `lg` the app shows one page title at a time, and it lives in the top
 * bar where the wordmark sits on Home — pages don't repeat it in their own
 * body, so their content starts a title's height higher. A page opts in by
 * calling `usePageHeader('Reflect')`; Home deliberately doesn't, which is what
 * leaves the wordmark showing there.
 *
 * Desktop is untouched: the top bar is `lg:hidden`, so the in-page headers
 * (kept, but `hidden lg:block`) remain the only titles at that width.
 */
interface PageHeaderState {
  title: string | null;
  setTitle: React.Dispatch<React.SetStateAction<string | null>>;
}

const PageHeaderContext = createContext<PageHeaderState | null>(null);

export const PageHeaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [title, setTitle] = useState<string | null>(null);
  const value = useMemo(() => ({ title, setTitle }), [title]);
  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
};

/** The title the current page published, or null when it published none (Home). */
export const usePageHeaderTitle = (): string | null =>
  useContext(PageHeaderContext)?.title ?? null;

/**
 * Publish this page's title to the mobile top bar for as long as the page is
 * mounted. Outside a provider (tests rendering a page in isolation, the design
 * lab) it is a no-op, so no page needs to know whether the shell is present.
 */
export const usePageHeader = (title: string): void => {
  const setTitle = useContext(PageHeaderContext)?.setTitle;
  useEffect(() => {
    if (!setTitle) return;
    setTitle(title);
    // Clear only if this page's title is still the one showing: on a route
    // change the outgoing page's cleanup can land after the incoming page has
    // published its own, and an unconditional reset would blank the new title.
    return () => setTitle(current => (current === title ? null : current));
  }, [title, setTitle]);
};
